import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Fragment, useEffect, useId, useState } from "react";
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTheme } from "@mui/material";
import { useForm, FormContainer, FormErrorProvider } from "react-hook-form-mui";
import { useI18N } from "../../context/I18NContext";
import { useFlow } from "../../context/FlowContext";
import { useSettings } from "../../context/SettingsContext";
import dayjs from "dayjs";
import 'dayjs/locale/nl';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import 'dayjs/locale/de';
import { CustomCheckboxElement, CustomTextFieldElement } from "../../layouts/Theme";
import useWidgetStore from "../../store/useWidgetStore";
import { useSubscriptions } from "../../hooks/query/useSubscriptions";
import { useCreateMandate } from "../../hooks/query/useCreateMandate";
import { useUpdateCart } from "../../hooks/query/useUpdateCart";
import ErrorAlert from "../ErrorAlert";
export default function MandateForm({ subscription }) {
    var _a;
    const theme = useTheme();
    const flow = useFlow();
    const { cart, consumer, language, setCart, setConsumer } = useWidgetStore();
    const { widgetMode } = useSettings();
    const errorsId = useId();
    const [error, setError] = useState();
    const [paymentError, setPaymentError] = useState();
    const { t } = useI18N();
    const formContext = useForm({});
    const subscriptionsQuery = useSubscriptions();
    const formContextCreateMandate = useForm({});
    const createMandateQuery = useCreateMandate(formContextCreateMandate);
    const formContextUpdateCart = useForm({});
    const updateCartQuery = useUpdateCart(formContextUpdateCart);
    useEffect(() => {
        if (!subscriptionsQuery.isRefetching) {
            subscriptionsQuery.refetch();
        }
    }, [consumer]);
    useEffect(() => {
        if (!subscriptionsQuery.isRefetching) {
            subscriptionsQuery.refetch();
        }
    }, []);
    useEffect(() => {
        if (subscriptionsQuery.data) {
            let subscriptions = subscriptionsQuery.data.result.items;
            setConsumer(Object.assign(Object.assign({}, consumer), { subscriptions: subscriptions }));
        }
    }, [subscriptionsQuery.data]);
    useEffect(() => {
        if (createMandateQuery.data) {
            let status = createMandateQuery.data.status, result = createMandateQuery.data.result.item;
            if (status.status !== "success") {
                if (status.userMessages) {
                    let _errors = [];
                    _errors.push(status.message);
                    Object.values(status.userMessages).map((error, i) => {
                        _errors.push(error);
                    });
                    setPaymentError(_errors);
                }
            }
            else if (result.redirect) {
                if (result.redirect === '#oa_thankyou') {
                    if (flow.onNext) {
                        flow.onNext();
                    }
                }
                else {
                    if (widgetMode === 'standalone') {
                        window.location.href = result.redirect;
                    }
                    else {
                        window.parent.postMessage({
                            type: 'redirect',
                            url: result.redirect
                        }, '*');
                    }
                    // window.location.href = result.redirect
                }
            }
            else {
                // get the membership we just got
                let subscription = {
                    subscriptionId: result.subscriptionId,
                    id: formContextCreateMandate.getValues('membershipTypeId'),
                    active: true
                };
                formContextUpdateCart.setValue('final', true);
                // @ts-ignore
                formContextUpdateCart.setValue('paymentMethod', `membership_${subscription.id}`);
                formContextUpdateCart.setValue('paymentOption', "full");
                formContextUpdateCart.setValue('selectedSubscription', subscription);
                updateCartQuery.refetch();
            }
        }
    }, [createMandateQuery.data]);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f;
        if (updateCartQuery.data) {
            let currentRoute = location.hash.substring(12);
            if (currentRoute.startsWith('mandate')) {
                let response = updateCartQuery.data;
                if (response.status.status === 'success') {
                    let result = response.result.item;
                    // This is the call that actually settles the mandate-paid order - the one Mandate.tsx
                    // made earlier only covered the registration fee. useUpdateCart's own setCart touches
                    // just the secureToken, so without this the membership/subscription line these lines
                    // now include never reaches the store, and the thank-you page shows an order with
                    // nothing in it. Same fields Mandate.tsx already syncs from this same response shape.
                    setCart(Object.assign(Object.assign({}, cart), { lines: result.lines, giftCards: result.giftCards, order_id: (_b = (_a = result.order) === null || _a === void 0 ? void 0 : _a.order_id) !== null && _b !== void 0 ? _b : cart.order_id, token: result.csrfToken }));
                    if ((result === null || result === void 0 ? void 0 : result.redirect) && ((_c = result === null || result === void 0 ? void 0 : result.redirect) === null || _c === void 0 ? void 0 : _c.uri)) {
                        if (result.redirect.uri === '#oa_thankyou') {
                            if (flow.onNext) {
                                flow.onNext();
                            }
                        }
                        else {
                            if (widgetMode === 'standalone') {
                                window.location.href = (_d = result === null || result === void 0 ? void 0 : result.redirect) === null || _d === void 0 ? void 0 : _d.uri;
                            }
                            else {
                                window.parent.postMessage({
                                    type: 'redirect',
                                    url: (_e = result === null || result === void 0 ? void 0 : result.redirect) === null || _e === void 0 ? void 0 : _e.uri
                                }, '*');
                            }
                            // window.location.href = result.redirect.uri
                        }
                    }
                }
                else if (response.status.status === 'failed') {
                    setError((_f = response.status.userMessages) !== null && _f !== void 0 ? _f : []);
                }
            }
        }
    }, [updateCartQuery.data]);
    useEffect(() => {
        if (consumer.mandate && consumer.mandate.status === "provisional") {
            formContext.setValue("holder", consumer.mandate.holder);
            formContext.setValue("iban", consumer.mandate.iban);
        }
    }, [consumer.mandate]);
    const onSubmit = (data, event) => {
        if (consumer.mandate && consumer.mandate.id && consumer.mandate.status === "provisional") {
            // approve our new mandate
            formContextCreateMandate.setValue("mandate", consumer.mandate);
            createMandateQuery.refetch();
        }
        else if (consumer.mandate && consumer.mandate.id) {
            formContextCreateMandate.setValue("mandateId", consumer.mandate.id);
            formContextCreateMandate.setValue("mandate", consumer.mandate);
            formContextCreateMandate.setValue("membershipTypeId", subscription.id);
            createMandateQuery.refetch();
        }
    };
    const onError = (errors) => { };
    return (_jsx(FormContainer, { formContext: formContext, onSuccess: onSubmit, onError: onError, children: _jsxs(FormErrorProvider, { onError: (error) => {
                if (error.type === 'validate') {
                    //  console.log(error)
                }
                return t(error.type, 'error');
            }, children: [error && (_jsx(Alert, { id: errorsId, sx: { my: 2 }, severity: "error", children: error === null || error === void 0 ? void 0 : error.map((_error, i) => (_jsx(Typography, { children: typeof _error === 'string' ? t(_error) : _error }, i))) })), ((_a = consumer.mandate) === null || _a === void 0 ? void 0 : _a.status) === "provisional" && (_jsxs(_Fragment, { children: [_jsx(Typography, { variant: "body1", sx: { color: theme.palette.text.primary, my: 1 }, children: t('Ik geef toestemming om de kosten van mijn abonnement van onderstaand rekeningnummer af te schrijven') }), _jsx(CustomTextFieldElement, { required: true, label: t('Naam rekeninghouder'), name: 'holder', type: "holder", sx: { my: 1 }, fullWidth: true, InputProps: {
                                readOnly: true
                            } }), _jsx(CustomTextFieldElement, { required: true, label: t('Rekeningnummer'), name: 'iban', type: "iban", sx: { my: 1 }, fullWidth: true, InputProps: {
                                readOnly: true
                            } }), _jsx(CustomCheckboxElement, { sx: {
                                marginLeft: 1
                            }, labelProps: {
                                sx: {
                                    color: theme.palette.text.primary,
                                    "& a": {
                                        color: theme.palette.text.primary,
                                    }
                                }
                            }, required: true, label: `${t('Ik ben akkoord met deze machtiging')} *`, name: 'confirm' }), _jsx(Button, { type: "submit", fullWidth: true, variant: "contained", children: t('Machtiging afgeven') })] })), consumer.mandate && consumer.mandate.status !== "provisional" && (_jsxs(_Fragment, { children: [paymentError && (_jsx(ErrorAlert, { messages: paymentError, setter: setPaymentError })), _jsx(Typography, { variant: "body1", sx: { color: theme.palette.text.primary, marginTop: 3, marginBottom: 1 }, children: t('Ik wil mijn bestaande machtiging uitbreiden voor dit abonnement.') }), _jsx(Typography, { variant: "h6", sx: { color: theme.palette.text.primary, my: 1 }, children: t('Mijn machtiging') }), _jsx(TableContainer, { component: Paper, sx: { marginBottom: 3 }, children: _jsx(Table, { sx: { width: '100%' }, size: "small", "aria-label": "a dense table", children: _jsxs(TableBody, { children: [_jsxs(TableRow, { sx: {
                                                backgroundColor: theme.palette.background.default,
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", children: t('Naam rekeninghouder') }), _jsx(TableCell, { component: "td", scope: "row", children: consumer.mandate.holder })] }), _jsxs(TableRow, { sx: {
                                                backgroundColor: theme.palette.background.default,
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", children: t('IBAN rekeningnummer') }), _jsx(TableCell, { component: "td", scope: "row", children: consumer.mandate.iban })] }), _jsxs(TableRow, { sx: {
                                                backgroundColor: theme.palette.background.default,
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }, children: [_jsx(TableCell, { component: "th", scope: "row", children: t('Datum afgifte') }), _jsx(TableCell, { component: "td", scope: "row", children: dayjs(consumer.mandate.signatureDate).locale(language).format("D MMMM YYYY") })] })] }) }) }), _jsx(Typography, { variant: "h6", sx: { color: theme.palette.text.primary, my: 1 }, children: t('Mijn abonnementen') }), _jsx(TableContainer, { component: Paper, sx: { marginBottom: 3 }, children: _jsx(Table, { sx: { width: '100%' }, size: "small", "aria-label": "a dense table", children: _jsxs(TableBody, { children: [consumer.mandate.membershipTypes.length > 0 && (_jsx(_Fragment, { children: consumer.mandate.membershipTypes.map((membershipType, i) => {
                                                var _a;
                                                let subscription = (_a = consumer === null || consumer === void 0 ? void 0 : consumer.subscriptions) === null || _a === void 0 ? void 0 : _a.find((subscription) => subscription.id === membershipType);
                                                if (!subscription) {
                                                    return (_jsx(Fragment, {}, i));
                                                }
                                                return (_jsx(_Fragment, { children: _jsx(TableRow, { sx: {
                                                            backgroundColor: theme.palette.background.default,
                                                            '&:last-child td, &:last-child th': { border: 0 }
                                                        }, children: _jsx(TableCell, { component: "th", scope: "row", children: subscription === null || subscription === void 0 ? void 0 : subscription.title }) }, i) }));
                                            }) })), consumer.mandate.membershipTypes.length === 0 && (_jsx(TableRow, { sx: {
                                                backgroundColor: theme.palette.background.default,
                                                '&:last-child td, &:last-child th': { border: 0 }
                                            }, children: _jsx(TableCell, { component: "th", scope: "row", children: t("Er zijn geen abonnementen aan je machtiging gekoppeld.") }) }))] }) }) }), _jsx(CustomCheckboxElement, { sx: {
                                marginLeft: 1
                            }, labelProps: {
                                sx: {
                                    color: theme.palette.text.primary,
                                    "& a": {
                                        color: theme.palette.text.primary,
                                    }
                                }
                            }, required: true, label: `${t('Ik geef toestemming om de kosten van mijn abonnement van dit rekeningnummer af te schrijven')} *`, name: 'confirm' }), _jsx(Button, { type: "submit", disabled: updateCartQuery.isFetching || createMandateQuery.isFetching, fullWidth: true, variant: "contained", children: t('Machtiging afgeven') })] }))] }) }));
}
